import {
    FRAGMENT_LISTING_DISCOUNT,
    FRAGMENT_STOREFRONT_EDGES,
} from '@marketplace/graphql/config/shared-fragments.js'
import { FRAGMENT_LISTING_PRODUCT_CARD } from '@marketplace/graphql/fragments/listing.js'
import gql from 'graphql-tag'

export const viewerQuery = gql`
    query viewerQuery {
        revision
        intendedUrl
        request {
            country
        }
        head
        viewer {
            retailerInvoices(first: 1) {
                totalCount
            }
            account {
                vouchers {
                    id
                    amount
                    minimumRetailerOrderTotal
                    isPercentage
                    endsAt
                    appliesTo
                    type
                }
                databaseId
                approvalStatus
                signupStatus
                city
                country
                cocNumber
                creditsafeId
                databaseId
                experimentGroup
                hasGlobalFreeShipping
                hasPayAfterArrival
                hasValidVatNumber
                houseNumber
                facebookUsername
                instagramUsername
                phone
                mobilePhone
                name
                companyName
                registeredCompanyName
                invoiceStreet
                invoiceHouseNumber
                invoicePostalCode
                invoiceCity
                retailerType
                postalCode
                street
                type
                url
                vatNumber
                isSmallBusiness
                eoriNumber
                email
                emailFinance
                hideEmailForMarketplace
                hideEmailForDropshipping
                unreadCount
                unreadDropshippingNotifications
                preferences {
                    amountPurchasedAnnually
                    amountPurchasedAnnuallyOnline
                    askAvgB2bTurnoverPerYear
                    askAvgB2cTurnoverPerYear
                    askSellingInCountries
                    businessSize
                    categories
                    elevatorPitch
                    incorporationYear
                    marketplaceViewType
                    onlineStoresCount
                    physicalStoresCount
                    settingUpStore
                    storeType
                    storeTypeDescription
                    primaryCategoryId
                    hasEuFulfilment
                    hasEuEntity
                    priceRange
                    softwarePos
                    softwareEcom
                    isInterestedInDropshipping
                }

                hasOrderchampPlus
                isTicaAccount
                shouldShowTrademartPartnershipModal
                ticaMatches
                ticaRetailer {
                    companyName
                    email
                    orderchampEmail
                    isConnected
                    isConfirmed
                    token
                    ticaRetailerNumber
                }
                iban
                bankHolder
                features
                isEligibleForDropshipping
                dropshipping {
                    status
                    skuLimit
                    skuCount
                    remainingSkuLimit
                    brandLimit
                    brandCount
                    remainingBrandLimit
                }
                subscription {
                    plan
                    name
                    price
                    discountedAmount
                    appliedCoupon {
                        times
                    }
                    cycleEndsAt
                    cancellationScheduledAt
                    expiresAt
                    renewsAt
                    isCancellationScheduled
                    endsAt
                    cancellableAt
                    paymentMethod
                    paymentDetails
                    processingFee
                    interval
                }
                channels(behavior: DROPSHIPPING) {
                    edges {
                        node {
                            id
                            status
                        }
                    }
                }
                signupIntent
                canStartConversations
                canApplyForBetterPricing
                instagramUsername
                url
                dropshippingInvitedByStorefront {
                    id
                    name
                    slug
                }
                hasPreviouslyPurchased
            }
            user {
                email
                firstName
                id
                isOrderchampEmployee
                lastName
                phone
                lastVisitedEnvironment
                timezone
                locale
            }
            isExcludedFromTracking
            totalProductsInCartCount
            unpaidRetailerOrder {
                id
                databaseId
                number
                totalPrice
                token
                currency
            }
            visiblePaymentMethods {
                id
                name
                logos
            }
            orderchampPlusPlans {
                name
                amount
                features {
                    slug
                    name
                    description
                }
            }
        }
    }
`

export const VIEWER_RECENTLY_BOUGHT_STOREFRONTS = gql`
    ${FRAGMENT_STOREFRONT_EDGES}
    query viewerRecentlyBoughtStorefrontsQuery {
        viewer {
            recentlyBoughtStorefronts(first: 20) {
                ...FragmentStorefrontEdges
            }
        }
    }
`

export const viewerRecentlyViewedListings = gql`
    ${FRAGMENT_LISTING_PRODUCT_CARD}
    query viewerRecentlyViewedListings {
        viewer {
            recentlyViewedListings(first: 10) {
                edges {
                    node {
                        ...FragmentListingProductCard
                    }
                }
            }
        }
    }
`

export const HOMEPAGE_PERSONAL_FEED_QUERY = gql`
    ${FRAGMENT_STOREFRONT_EDGES}
    ${FRAGMENT_LISTING_PRODUCT_CARD}
    query homepagePersonalFeed {
        viewer {
            newestStorefronts(first: 10) {
                ...FragmentStorefrontEdges
            }
            recentlyViewedListings(first: 10) {
                edges {
                    node {
                        ...FragmentListingProductCard
                    }
                }
            }
        }
    }
`

export const viewerRecommendedListingsQuery = gql`
    ${FRAGMENT_LISTING_DISCOUNT}
    query viewerRecommendedListings($listingId: ID!) {
        viewer {
            recommendedListings: listingRecommendations(
                first: 15
                listingId: $listingId
            ) {
                edges {
                    node {
                        ... on Listing {
                            ...FragmentGlobalDiscount
                            availableAt
                            brand
                            databaseId
                            id
                            inStock
                            isFavorite
                            msrpMin
                            priceMin
                            title
                            description
                            translation {
                                description
                            }
                            url
                            images(first: 1) {
                                edges {
                                    node {
                                        id
                                        x1: transformedUrl(
                                            width: 400
                                            height: 400
                                        )
                                        x2: transformedUrl(
                                            width: 800
                                            height: 800
                                        )
                                    }
                                }
                            }
                            storefront {
                                id
                                minimumOrderAmount
                                slug
                                discountPercentage
                                campaignDiscount {
                                    id
                                    campaign
                                    percentage
                                    createdAt
                                    orderchampContributionPercentage
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export const VIEWER_ORDERCHAMP_PLUS = gql`
    query viewerOrderchampPlus {
        viewer {
            account {
                hasOrderchampPlus
                orderchampPlus {
                    isActive
                    endsAt
                    paymentStatus {
                        state
                        redirectUrl
                    }
                    plan {
                        slug
                        amount
                        description
                        features {
                            slug
                        }
                    }
                }
            }
        }
    }
`

export const EMAIL_SETTINGS_QUERY = gql`
    query getAccountEmailSettings($token: String) {
        getAccountEmailSettings(token: $token) {
            identifier
            field
            label
            description
            isEnabled
        }
    }
`
